/**
 * Raw API Endpoint Path Strings
 * Single source of truth for every API path in the application.
 * These are plain strings -- no logic, no URL building. Use the typed
 * endpoint getter modules (admin.ts, user.ts, etc.) for constructed paths.
 */

export const API_ENDPOINTS = {
  // Authentication endpoints
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    profile: '/auth/profile',
    register: '/auth/register',
    checkEmail: '/auth/check-email',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    verifyTwoFactor: '/auth/verify-2fa',
    resendTwoFactor: '/auth/2fa/resend',
    emailVerificationStatus: '/auth/email-verification-status',
    resendVerificationEmail: '/auth/resend-verification-email'
  },

  // Admin endpoints
  admin: {
    // Auth endpoints (admin-specific, OTP passwordless flow)
    auth: {
      requestOtp: '/admin/auth/login/request-otp',
      verifyOtp: '/admin/auth/login/verify-otp',
      logout: '/admin/auth/logout',
      refresh: '/admin/auth/refresh',
      profile: '/admin/auth/profile'
    },

    // Admin dashboard endpoints
    dashboard: {
      stats: '/admin/dashboard/stats',
      activity: '/admin/dashboard/activity',
      overview: '/admin/dashboard/overview',
      donations: {
        analytics: '/admin/dashboard/donations/analytics',
        trends: '/admin/dashboard/donations/trends',
        activity: '/admin/dashboard/donations/activity',
        cacheHealth: '/admin/dashboard/donations/cache/health'
      }
    },

    // Admin management endpoints
    users: '/admin/users',
    campaigns: '/admin/campaigns',
    donations: '/admin/donations',
    donationsStats: '/admin/donations/stats',
    donationsHealth: '/admin/donations/health',
    donationsTrends: '/admin/donations/trends',
    donationsActivity: '/admin/donations/activity',
    donationsCache: {
      invalidate: '/admin/donations/cache/invalidate',
      health: '/admin/donations/cache/health'
    },
    paymentRetry: {
      create: '/admin/payment-retry',
      list: '/admin/payment-retry',
      detail: '/admin/payment-retry', // /:jobId appended in service
      cancel: '/admin/payment-retry', // /:jobId/cancel appended in service
      pause: '/admin/payment-retry', // /:jobId/pause appended in service
      resume: '/admin/payment-retry', // /:jobId/resume appended in service
      process: '/admin/payment-retry', // /:jobId/process appended in service
      metrics: '/admin/payment-retry/metrics'
    },
    retryAnalytics: {
      analytics: '/admin/retry-analytics/analytics',
      distribution: '/admin/retry-analytics/analytics/distribution',
      errorBreakdown: '/admin/retry-analytics/analytics/error-breakdown',
      attempts: '/admin/retry-analytics/attempts',
      attemptDetail: '/admin/retry-analytics/attempts' // /:attemptId appended in service
    },
    exports: {
      progress: '/admin/exports/progress',
      progressStream: '/admin/exports/progress/stream',
      jobs: '/admin/exports/jobs',
      pause: '/admin/exports/jobs', // /:jobId/pause appended in service
      resume: '/admin/exports/jobs', // /:jobId/resume appended in service
      cancel: '/admin/exports/jobs', // /:jobId/cancel appended in service
      cleanup: '/admin/exports/cleanup'
    },
    receipts: {
      secureUrl: '/admin/receipts/secure-url',
      secure: '/admin/receipts/secure',
      bulk: '/admin/donations/receipts/bulk'
    },
    blog: '/admin/blog',

    // Admin blog endpoints
    blogAdmin: {
      stats: '/admin/blog/stats',
      posts: '/admin/blog/posts',
      categories: '/admin/blog/categories',
      authors: '/admin/blog/authors',
      comments: '/admin/blog/comments',
      media: '/admin/blog/media',
      seo: '/admin/blog/seo',
      analytics: '/admin/blog/analytics'
    },

    // Admin newsletter endpoints
    newsletter: {
      subscriptions: '/admin/newsletter/subscriptions',
      stats: '/admin/newsletter/stats'
    },

    // Admin contact endpoints
    contact: {
      submissions: '/admin/contact/submissions',
      stats: '/admin/contact/submissions/stats'
    },

    // Admin chat endpoints
    chat: {
      conversations: '/admin/chat/conversations',
      stats: '/admin/chat/stats'
    },

    // HajiPay Gateway endpoints
    gateway: {
      projects: '/admin/gateway/projects'
    },

    // Admin Wallet Management endpoints
    wallets: {
      list: '/admin/wallets',
      detail: '/admin/wallets', // + :id
      freeze: '/admin/wallets', // + :id/freeze
      unfreeze: '/admin/wallets', // + :id/unfreeze
      adjust: '/admin/wallets', // + :id/adjust
      transactions: '/admin/wallets', // + :id/transactions
      payouts: '/admin/wallets' // + :id/payouts
    },

    kycReview: {
      pending: '/admin/kyc/pending',
      approve: '/admin/kyc', // + /:profileId/approve
      reject: '/admin/kyc',
      requestInfo: '/admin/kyc',
      revoke: '/admin/kyc',
    },
    payoutReview: {
      pending: '/admin/payout-accounts/pending',
      verify: '/admin/payout-accounts',
      reject: '/admin/payout-accounts',
    },

    // Gazian Water Admin endpoints
    gazian: {
      orders: '/admin/gazian/orders',
      orderStats: '/admin/gazian/orders/stats',
      customers: '/admin/gazian/customers',
      shipping: {
        labels: '/admin/gazian/shipping/labels',
        rates: '/admin/gazian/shipping/rates'
      }
    },

    // Admin Notifications endpoints
    notifications: {
      list: '/admin/notifications',
      unreadCount: '/admin/notifications/unread-count',
      markRead: '/admin/notifications', // + /:id/read
      markAllRead: '/admin/notifications/read-all',
      dismiss: '/admin/notifications' // + /:id
    },

    // Admin Campaign Categories endpoints
    campaignCategories: '/admin/campaign-categories',

    // Admin Curated Causes endpoints
    causes: '/admin/causes',

    // Admin Changelog endpoints
    changelog: '/admin/changelog',

    // Admin Campaign Updates moderation
    campaignUpdates: '/admin/campaign-updates', // + :updateId (DELETE)

    // Legal Consent Admin endpoints (policy version management)
    policies: {
      list: '/admin/policies',
      create: '/admin/policies',
      activate: '/admin/policies', // + /:id/activate
      userConsent: '/admin/policies/consent/users' // + /:userId
    },

    // Identity Verification Admin endpoints
    verification: {
      status: '/verification/admin/status', // + /:userId (GET)
      history: '/verification/admin/history', // (GET) paginated
      statistics: '/verification/admin/statistics', // (GET)
      sseStats: '/verification/admin/sse-stats', // (GET)
      sync: '/verification/admin/sync', // + /:sessionId (POST)
      resetAttempts: '/verification/admin/reset-attempts', // + /:userId (POST)
      userSessions: '/verification/admin/user-sessions', // + /:userId (GET)
      forceVerify: '/verification/admin/force-verify', // + /:userId (POST)
      stuck: '/verification/admin/stuck', // (GET)
      bulkSync: '/verification/admin/bulk-sync', // (POST)
      sessionDetail: '/verification/admin/session-detail', // + /:sessionId (GET)
      sessionImageLinks: '/verification/admin/session-detail' // + /:sessionId/image-links (POST)
    }
  },

  // User auth endpoints (regular users)
  user: {
    auth: {
      login: '/auth/login',
      logout: '/auth/logout',
      refresh: '/auth/refresh',
      register: '/auth/register',
      profile: '/auth/me',
      anonymous: '/auth/anonymous',
      verifyEmail: '/auth/verify-email',
      forgotPassword: '/auth/forgot-password',
      verifyResetCode: '/auth/verify-reset-code',
      resetPassword: '/auth/reset-password',
      verifyTwoFactor: '/auth/verify-2fa',
      resendTwoFactor: '/auth/2fa/resend',
      // Email OTP (Primary auth method)
      otpSend: '/auth/otp/send',
      otpVerify: '/auth/otp/verify',
      // Passkey/WebAuthn (Secondary auth method)
      passkeyLoginOptions: '/auth/passkey/login/options',
      passkeyLoginVerify: '/auth/passkey/login/verify',
      passkeyRegisterOptions: '/auth/passkey/register/options',
      passkeyRegisterVerify: '/auth/passkey/register/verify',
      passkeys: '/auth/passkeys',
      // Phone OTP (Account recovery fallback)
      phoneSend: '/auth/phone/send',
      phoneVerify: '/auth/phone/verify',
      // Email validation status (SSE endpoint)
      validationStatus: '/auth/validation-status',
      // Social login (Google, Apple)
      social: '/auth/social'
    },
    profile: {
      get: '/profile',
      update: '/profile',
      completion: '/profile/completion',
      avatar: '/profile/avatar',
      organizationLogo: '/profile/organization-logo',
      verificationStatus: '/profile/verification-status'
    },
    campaigns: '/user/campaigns',
    donations: {
      list: '/user/donations',
      statistics: '/user/donations/statistics',
      detail: '/user/donations', // /:id appended in service
      receipt: '/user/donations' // /:id/receipt appended in service
    },
    stats: '/user/stats',
    context: '/user/context',
    settings: '/user/settings',
    // Verification endpoints
    verification: {
      session: '/verification/session',
      status: '/verification/status',
      statusRefresh: '/verification/status/refresh',
      statusStream: '/verification/status/stream', // SSE endpoint for real-time updates
      canCreateCampaigns: '/verification/can-create-campaigns',
      canDonate: '/verification/can-donate'
    },
    /** Direct-pay compliance (manual KYC + bank payout + settlements) */
    kyc: {
      profile: '/user/kyc/profile',
      submit: '/user/kyc/submit',
      documents: '/user/kyc/documents',
    },
    payoutAccount: {
      get: '/user/payout-account',
      draft: '/user/payout-account/draft',
      submit: '/user/payout-account/submit',
      paypal: {
        get: '/user/payout-account/paypal',
        referral: '/user/payout-account/paypal/referral',
        return: '/user/payout-account/paypal/return',
        refreshStatus: '/user/payout-account/paypal/refresh-status',
        loa: '/user/payout-account/paypal/loa',
        loaFile: '/user/payout-account/paypal/loa/file',
      },
    },
    settlements: {
      list: '/user/settlements',
      markSent: '/user/settlements/donations', // + /:donationId/mark-sent
      confirmReceived: '/user/settlements/donations', // + /:donationId/confirm-received
    },
    donorSettlements: {
      byIntent: '/donor/settlements/by-intent', // + /:intentKey
    },
    // Legal Consent endpoints (user-facing, GDPR compliance)
    consent: {
      status: '/consent',
      history: '/consent/history',
      grant: '/consent/grant',
      withdraw: '/consent/withdraw'
    },
    // Notification Preferences endpoints
    notificationPreferences: {
      get: '/users/me/notification-preferences',
      update: '/users/me/notification-preferences',
      marketingConsent: '/users/me/notification-preferences/marketing-consent',
      channels: '/users/me/notification-preferences/channels',
      modes: '/users/me/notification-preferences/modes',
      health: '/users/me/notification-preferences/health'
    }
  },

  // User notification endpoints
  notifications: {
    list: '/notifications',
    unreadCount: '/notifications/unread-count',
    markRead: '/notifications', // + /:id/read
    markAllRead: '/notifications/read-all',
    dismiss: '/notifications', // + /:id
    stream: '/notifications/stream' // SSE endpoint
  },

  // Campaign management endpoints
  campaigns: {
    list: '/user/campaigns',
    create: '/user/campaigns',
    draft: '/user/campaigns/draft', // POST -- create minimal DRAFT from guest wizard
    get: '/user/campaigns', // + :id
    update: '/user/campaigns', // + :id
    delete: '/user/campaigns' // + :id
  },

  // Media upload endpoints
  uploads: {
    campaignMedia: '/uploads/campaigns', // + :campaignId/media
    batchUpload: '/uploads/campaigns', // + :campaignId/media/batch
    mediaStats: '/uploads/campaigns', // + :campaignId/media/stats
    deleteMedia: '/uploads/campaigns', // + :campaignId/media/:mediaId
    presignedUrl: '/uploads/media' // + :mediaId/presigned-url
  },

  // Donation endpoints
  donations: {
    createIntent: '/hajiq-pay/donations',
    getStatus: '/hajiq-pay/donations',
    getReceipt: '/hajiq-pay/donations',
    downloadReceipt: '/hajiq-pay/donations',
    emailReceipt: '/hajiq-pay/donations',
    confirmEmail: '/hajiq-pay/donations',
    subscriptions: '/hajiq-pay/subscriptions',
    setupIntent: '/hajiq-pay/billing/setup-intent'
  },

  // Public endpoints
  public: {
    campaigns: '/campaigns',
    campaignCategories: '/campaigns/categories',
    blog: '/blog',
    contact: '/contact',
    help: '/help',
    changelog: '/changelog'
  },

  // Curated causes endpoints (uses versioned API)
  causes: {
    curated: '/causes/curated',
    detail: '/causes' // + :slug
  },

  // Contact endpoints
  contact: {
    submit: '/contact/submit'
  },

  // Chat endpoints
  chat: {
    send: '/chat/send',
    conversations: '/chat/conversations',
    conversation: '/chat/conversations', // + :id
    socket: '/chat' // WebSocket endpoint path
  },

  // Newsletter endpoints
  newsletter: {
    subscribe: '/newsletter/subscribe'
  },

  // Blog endpoints
  blog: {
    // Public blog endpoints
    public: {
      info: '/blog',
      posts: '/blog/posts',
      categories: '/blog/categories',
      authors: '/blog/authors',
      search: '/blog/search',
      sitemap: '/blog/sitemap',
      robots: '/blog/robots.txt'
    },

    // Comment endpoints
    comments: '/blog/posts'
  },

  // Social features endpoints
  social: {
    favorites: '/user/campaigns', // + :campaignId/favorite
    bookmarks: '/user/campaigns', // + :campaignId/bookmark
    shares: '/campaigns', // + :slug/share
    timeline: '/campaigns' // + :slug/timeline
  },

  // Payment endpoints
  payments: {
    stripe: {
      createIntent: '/payments/stripe/create-intent',
      confirmPayment: '/payments/stripe/confirm',
      webhooks: '/payments/stripe/webhooks'
    },
    // GoCardless custom payment pages (bank payments)
    institutions: '/payments/institutions',
    customOneTime: '/payments/custom/one-time',
    customRecurring: '/payments/custom/recurring'
  },

  // Crowdfunding (Launchpad) endpoints
  crowdfunding: {
    // Public endpoints (no auth, no version prefix)
    public: {
      projects: '/crowdfunding/projects',
      stats: '/crowdfunding/projects/stats',
      categories: '/crowdfunding/categories'
    },
    // User/creator endpoints (auth required, versioned)
    user: {
      projects: '/user/crowdfunding/projects',
      pledges: '/user/crowdfunding/pledges',
      comments: '/user/crowdfunding/comments',
      stats: '/user/crowdfunding/stats'
    },
    // Admin endpoints (admin auth, versioned)
    admin: {
      projects: '/admin/crowdfunding/projects',
      categories: '/admin/crowdfunding/categories',
      comments: '/admin/crowdfunding/comments'
    }
  },

  // Halal business directory listings
  // NOTE: no money flow -- discovery ends in contacting the business directly.
  // See docs/implementation-plans/2026/07/halal-directory/00-MASTER.md D1.
  listings: {
    // Public endpoints (no auth, no version prefix -- matches /api/campaigns).
    // Returns PUBLISHED rows only; status is server-forced, never a parameter.
    public: {
      listings: '/listings',
      // Map-viewport pins. Deliberately a SEPARATE endpoint from the list: it
      // returns a lean pin payload (id, slug, title, lat, lng, categorySlug) and
      // nothing else, because a viewport holds hundreds of pins and payload size
      // is the difference between a map that pans and one that stutters.
      listingsMap: '/listings/map',
      // The category tile row. Returns only ACTIVE categories that hold at least
      // one PUBLISHED listing (empty categories never reach the client), each
      // with a listingCount. Slug-keyed; the frontend translates the label via
      // t(`categories.${slug}`) and uses this response's `name` as the fallback.
      listingsCategories: '/listings/categories'
    }
  },

  // Fundasister -- Shariah-compliant women entrepreneur funding
  fundasister: {
    // Public endpoints (no auth required)
    public: {
      dashboard: '/fundasister/dashboard',
      directory: '/fundasister/directory',
      pools: '/fundasister/pools',
      venturePublic: '/fundasister/ventures' // /:id/public appended in service
    },
    // User endpoints (user JWT required)
    user: {
      apply: '/fundasister/apply',
      myApplication: '/fundasister/my-application',
      myApplications: '/fundasister/my-applications',
      profitReport: '/fundasister/profit-report',
      myVenture: '/fundasister/my-venture',
      myDonations: '/fundasister/my-donations',
      // Stripe-based donation flow for users (poolContribute is admin-only)
      poolDonate: '/fundasister/pool/donate'
    },
    // Admin endpoints (admin JWT required)
    admin: {
      applications: '/admin/fundasister/applications',
      ventures: '/admin/fundasister/ventures',
      pool: '/admin/fundasister/pool',
      profitReports: '/admin/fundasister/profit-reports'
    }
  }
} as const;
